import { useRef, useState } from 'react';
import CustomerSearchModal from '../../Shared/Customers/CustomerSearchModal';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { actionCRM, postCRM, resetCRMStatus } from './CRMSlice';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../../../firebase';
import { Alert, IconButton, InputAdornment, OutlinedInput, Slide, SlideProps, Snackbar } from '@mui/material';
import { Search } from '@mui/icons-material';
import { Button, Container, Input, Label } from 'reactstrap';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import './CRM.css';

const CRMEntry = () => {
    const noteRef = useRef(null);
    const custRef = useRef(null);
    const [createFollowUp, setCreateFollowUp] = useState<boolean>(false);
    const [date, setDate] = useState<Date | null>(new Date());
    const [custID, setCustID] = useState<string>('');
    const [note, setNote] = useState<string>('');
    const [custErrorMessage, setCustErrorMessage] = useState<boolean>(false);
    const [noteErrorMessage, setNoteErrorMessage] = useState<boolean>(false);
    const [modalVisible, setModalVisible] = useState<boolean>(false);
    const errMsg = useAppSelector((state) => state.crm.errMsg);
    const [ reqFieldToast, setReqFieldToast ] = useState<boolean>(false);
    const [ closeErrorToast, setCloseErrorToast ] = useState<boolean>(false);
    const [ successToast, setSuccessToast ] = useState<boolean>(false);
    const dispatch = useAppDispatch();

    const onClose = (id: string | null) => {
        setModalVisible(false);
        setCustID(id || '');
    }

    const handleSubmit = async () => {
        
        if (custID && createFollowUp && date && note) {
            console.log(`CRM ENTRY CALENDAR: ${note}, ${date}, ${custID}`)

            try {
                await dispatch(actionCRM({custID, note})).unwrap();

                setSuccessToast(true);
                setCustID('');
                setNote('');
                setCustErrorMessage(false);
                setNoteErrorMessage(false);
                setCreateFollowUp(false);
                setDate(new Date());

            } catch (err: any) {
                console.error('CRM ACTION ERROR:', err)
                setCloseErrorToast(true);
            }
        } else if (!createFollowUp && custID && note) {
            try {
                await dispatch(postCRM({custID, note})).unwrap();

                setSuccessToast(true);

                dispatch(resetCRMStatus());
                setCustID('');
                setNote('');
                setCustErrorMessage(false);
                setNoteErrorMessage(false);
                setCreateFollowUp(false);
                setDate(new Date());
            } catch (e: any) {
                console.error('POST CRM ERROR', e.message);

                setCloseErrorToast(true);
            }

        } else {  
            if (!custID) {
                setCustErrorMessage(true);
                setReqFieldToast(true);
                return;
            }

            if (!note) {
                setNoteErrorMessage(true);
                setReqFieldToast(true);
                return;
            }

            setSuccessToast(true);

            setCustID('');
            setNote('');
            setCustErrorMessage(false);
            setNoteErrorMessage(false);
            setCreateFollowUp(false);
            setDate(new Date());
        }
    };


    const handleCustomerSearch = async () => {
        setModalVisible(true);
        logEvent(analytics, 'crmcust_search_web', {
            name: 'CRM Customer Search',
            description: 'User used customer search'
        });
    }

    const handleCRMAction = async () => {
        setCreateFollowUp(!createFollowUp);
        logEvent(analytics, 'crm_action_web', {
            name: 'CRM Action checkmark',
            description: 'User pressed CRM Follow-up'
        });
    };

    const handleToastClose = ( 
        event?: React.SyntheticEvent | Event, 
        reason?: string 
    ) => { 
        if (reason === 'clickaway') return; 
        setReqFieldToast(false);
        setCloseErrorToast(false);
        setSuccessToast(false);
    };

    const SlideTransition = (props: SlideProps) => {
        return <Slide {...props} direction="down" />
    };

    return (
        <>
            <Snackbar
                open={reqFieldToast}
                onClose={handleToastClose}
                autoHideDuration={3000}
                anchorOrigin={{ vertical: 'top', horizontal: 'center', }}
                slots={{
                    transition: SlideTransition
                }}
            >
                <Alert
                    onClose={handleToastClose}
                    severity='error'
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    Missing Required Field!
                </Alert>
            </Snackbar>
            <Snackbar
                open={closeErrorToast}
                onClose={handleToastClose}
                autoHideDuration={3000}
                anchorOrigin={{ vertical: 'top', horizontal: 'center', }}
                slots={{
                    transition: SlideTransition
                }}
            >
                <Alert
                    onClose={handleToastClose}
                    severity='error'
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {errMsg}
                </Alert>
            </Snackbar>
            <Snackbar
                open={successToast}
                onClose={handleToastClose}
                autoHideDuration={3000}
                anchorOrigin={{ vertical: 'top', horizontal: 'center', }}
                slots={{
                    transition: SlideTransition
                }}
            >
                <Alert
                    onClose={handleToastClose}
                    severity='success'
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    CRM Submitted
                </Alert>
            </Snackbar>
            <Container className='pt-4'>
                <div>
                    <OutlinedInput 
                        id='custId'
                        style={{ width: '100%' }}
                        value={custID}
                        className='bg-white shadow-sm'
                        onChange={(e) => setCustID(e.target.value)}
                        placeholder='Customer ID'
                        required
                        error={custErrorMessage}
                        endAdornment={
                            <InputAdornment position='end'>
                                <IconButton
                                    onClick={handleCustomerSearch}
                                    edge='end'
                                >
                                    <Search />
                                </IconButton>
                            </InputAdornment>
                        }
                    />
                    <div className='text-start'>
                        {custErrorMessage && <span style={{ color: 'red', left: 10, }}>This field is required!</span>}
                        {errMsg && <span style={{ color: 'red', left: 10 }}>{errMsg}</span>}
                    </div>
                </div>
                <div 
                    style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        marginBlock: 10,
                    }}
                >
                    <Input
                        id="followUp"
                        type="checkbox"
                        className="mx-2 color-primary"
                        color="primary"
                        checked={createFollowUp}
                        onChange={handleCRMAction}
                    />
                    <Label 
                        check
                        for="followUp"
                    >
                        Create Action
                    </Label>
                    {createFollowUp && 
                        <div style={{ flexDirection: 'row', alignItems: 'center', marginLeft: 20 }}>
                            <span>Follow-Up Date: </span>
                            <DatePicker
                                showIcon
                                toggleCalendarOnIconClick
                                selected={date} 
                                onChange={(date: Date | null) => setDate(date)}
                                icon='fa fa-calendar'
                            />
                        </div>
                    }
                </div>
                <div>
                    <OutlinedInput 
                        ref={noteRef}
                        type='text'
                        style={{ width: '100%', minHeight: 100 }}
                        value={note}
                        className='bg-white shadow-sm'
                        onChange={(e) => setNote(e.target.value)}
                        placeholder='CRM Note'
                        multiline
                        error={noteErrorMessage}
                        required
                        sx={{
                            alignItems: 'flex-start',
                            '& textarea': {
                            verticalAlign: 'top',
                            },
                        }}
                    />
                    <div className='text-start'>
                        {noteErrorMessage && <span style={{ color: 'red', left: 10, }}>This field is required!</span>}
                    </div>
                </div>
                <div 
                    style={{ 
                        flexDirection: 'row', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        position: 'relative',
                        marginBlockStart: 20,
                    }}
                >
                    <Button 
                        color="primary w-25 fw-bold"
                        onClick={handleSubmit}
                    >
                        Submit
                    </Button>
                </div>
            </Container>
            {modalVisible && 
                <CustomerSearchModal 
                    visible={modalVisible} 
                    onClose={(id: string | null) => onClose(id)}
                />
            }
        </>
    )
};

export default CRMEntry;